import {
  FileText,
  LineChart,
  Ship,
  Bot,
  BarChart3,
  BadgeDollarSign,
  ShieldCheck,
  Calculator,
  Zap,
  Target,
  TrendingUp,
  Eye,
  BrainCircuit,
  MessageSquare,
  LayoutDashboard,
  ArrowRight,
  Check,
  Quote,
  Star,
  Users,
  AlertTriangle,
  Layers,
  Globe,
  Lock,
  Workflow,
  RefreshCw,
  FileBarChart,
  CircleDollarSign,
} from 'lucide-react';

/* ================================================================
   SALES BROCHURE — HTML/Tailwind (imprimible a PDF via window.print)
   ================================================================ */

/* Reusable logo for light & dark backgrounds */
function PrixiaLogo({
  variant = 'dark',
  size = 'md',
}: {
  variant?: 'dark' | 'white';
  size?: 'sm' | 'md' | 'lg';
}) {
  const src = variant === 'white' ? '/prixia-logo-white.svg' : '/prixia-logo.svg';
  const h = size === 'sm' ? 'h-7' : size === 'lg' ? 'h-14' : 'h-9';
  return <img src={src} alt="Prixia" className={`${h} w-auto`} />;
}

/* Page footer */
function PageFooter({ page, variant = 'light' }: { page: number; variant?: 'light' | 'dark' }) {
  const color = variant === 'dark' ? 'text-indigo-300/60' : 'text-slate-400';
  return (
    <div className={`mt-auto pt-4 flex items-center justify-between text-[8px] ${color}`}>
      <PrixiaLogo variant={variant === 'dark' ? 'white' : 'dark'} size="sm" />
      <span>prixia.com — Material confidencial</span>
      <span>{page}</span>
    </div>
  );
}

export function SalesBrochureHTML() {
  return (
    <div className="w-[210mm] bg-white text-slate-800 font-[Inter,system-ui,sans-serif] text-[11px] leading-relaxed print:shadow-none shadow-2xl">
      {/* ─── PAGE 1: COVER ─── */}
      <section className="h-[297mm] relative overflow-hidden flex flex-col">
        {/* Gradient header */}
        <div className="h-[48%] bg-gradient-to-br from-indigo-700 via-indigo-600 to-violet-600 relative overflow-hidden flex flex-col justify-center px-14 text-white">
          {/* Decorative circles */}
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/5" />
          <div className="absolute top-32 -left-16 w-48 h-48 rounded-full bg-white/5" />
          <div className="absolute bottom-10 right-24 w-24 h-24 rounded-full bg-white/10" />

          <div className="relative z-10">
            <div className="text-[11px] font-semibold tracking-[3px] uppercase text-indigo-200 mb-3">
              Plataforma SaaS de Pricing Intelligence
            </div>
            <h1 className="text-[44px] font-extrabold leading-[1.08] mb-5 tracking-tight">
              Transforma tu
              <br />
              estrategia de
              <br />
              precios con IA
            </h1>
            <p className="text-[14px] text-indigo-100 max-w-[420px] leading-relaxed">
              Cotiza en minutos, monitorea competidores 24/7, controla costos de importación y toma
              cada decision de pricing respaldada por inteligencia artificial y datos en tiempo
              real.
            </p>
          </div>

          {/* Logo */}
          <div className="absolute top-8 right-10 z-10">
            <PrixiaLogo variant="white" size="lg" />
          </div>
        </div>

        {/* Dashboard screenshot */}
        <div className="flex-1 relative px-14 -mt-14">
          <div className="rounded-xl overflow-hidden shadow-2xl border border-slate-200 bg-white">
            <img
              src="/screenshots/20-dashboard.png"
              alt="Dashboard Prixia"
              className="w-full h-auto"
            />
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            {[
              {
                value: '85%',
                label: 'Menos tiempo cotizando',
                sub: 'vs. procesos manuales',
                color: 'text-indigo-600',
              },
              {
                value: '4x',
                label: 'Más rápido que Excel',
                sub: 'en generación de propuestas',
                color: 'text-teal-600',
              },
              {
                value: '32%',
                label: 'Mejora en márgenes',
                sub: 'promedio en 6 meses',
                color: 'text-violet-600',
              },
              {
                value: '0',
                label: 'Errores de calculo',
                sub: 'formulas automaticas',
                color: 'text-emerald-600',
              },
            ].map((s) => (
              <div
                key={s.label}
                className="text-center p-3 rounded-lg bg-slate-50 border border-slate-100"
              >
                <div className={`text-[22px] font-extrabold ${s.color}`}>{s.value}</div>
                <div className="text-[10px] font-semibold text-slate-700 mt-0.5">{s.label}</div>
                <div className="text-[8.5px] text-slate-400">{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Tagline */}
          <p className="text-center text-[10px] text-slate-400 mt-4">
            Utilizado por distribuidoras, empresas de tecnologia, manufactura y comercio mayorista
            en Latinoamerica
          </p>
        </div>

        {/* Footer */}
        <div className="px-14 pb-4 flex items-center justify-between text-[8px] text-slate-400">
          <PrixiaLogo variant="dark" size="sm" />
          <span>prixia.com — Material confidencial</span>
        </div>
      </section>

      {/* ─── PAGE 2: PROBLEMA ─── */}
      <section className="h-[297mm] px-14 py-12 flex flex-col break-before-page">
        <div className="text-xs font-bold tracking-[3px] uppercase text-red-500 mb-2">
          El Problema
        </div>
        <h2 className="text-[28px] font-extrabold text-slate-900 leading-tight mb-2">
          Tu equipo de ventas
          <br />
          esta perdiendo dinero
        </h2>
        <p className="text-[12px] text-slate-500 max-w-[520px] mb-6 leading-relaxed">
          La mayoria de las empresas B2B siguen cotizando con procesos manuales, sin visibilidad de
          precios del mercado y sin control real sobre sus márgenes. Esto genera perdidas
          silenciosas que impactan directamente en la rentabilidad.
        </p>

        <div className="grid grid-cols-2 gap-5 mb-5">
          {[
            {
              icon: AlertTriangle,
              color: 'bg-red-50 text-red-500 border-red-100',
              title: 'Cotizaciones en Excel',
              desc: 'Procesos manuales lentos con errores de calculo que erosionan márgenes. Un vendedor promedio pierde 6 horas semanales armando cotizaciones en planillas. Copiar y pegar datos entre archivos genera inconsistencias que nadie detecta hasta que el cliente reclama.',
              stat: '6 hrs/semana perdidas por vendedor',
            },
            {
              icon: Eye,
              color: 'bg-amber-50 text-amber-600 border-amber-100',
              title: 'Cero visibilidad competitiva',
              desc: 'Tu competencia cambia precios a diario en plataformas de e-commerce y tu te enteras días después. Sin monitoreo automatizado, siempre estas reaccionando en vez de anticipando. Perdiendo deals por precios desactualizados que podrias haber ajustado a tiempo.',
              stat: 'Reacción tardia al mercado',
            },
            {
              icon: Ship,
              color: 'bg-blue-50 text-blue-500 border-blue-100',
              title: 'Costos de importación opacos',
              desc: 'Aranceles, fletes, seguros, impuestos y gastos portuarios calculados a mano o estimados "por experiencia". Los costos landed aproximados generan márgenes reales desconocidos. Un error del 3% en el costo landed puede significar cotizar por debajo del costo sin darse cuenta.',
              stat: 'Márgenes incorrectos en cada cotización',
            },
            {
              icon: Users,
              color: 'bg-violet-50 text-violet-500 border-violet-100',
              title: 'Descuentos sin control',
              desc: 'Cada vendedor aplica sus propios descuentos sin una politica clara y centralizada. Las aprobaciones se gestionan por WhatsApp o email sin dejar registro auditable. Esto genera diferencias de hasta 40% en precios del mismo producto según quien cotice.',
              stat: 'Hasta 40% de variación entre vendedores',
            },
          ].map((p) => (
            <div
              key={p.title}
              className={`rounded-xl border p-5 flex flex-col ${p.color.split(' ')[0]} ${p.color.split(' ').slice(2).join(' ')}`}
            >
              <div className="flex items-start gap-3 mb-2">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${p.color}`}
                >
                  <p.icon className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-slate-900 text-[13px] mt-2">{p.title}</h3>
              </div>
              <p className="text-slate-600 text-[10px] leading-[1.7] flex-1">{p.desc}</p>
              <div className="mt-3 pt-2.5 border-t border-slate-200/60">
                <span className="text-[9.5px] font-bold text-slate-700">{p.stat}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom callout */}
        <div className="p-5 rounded-xl bg-gradient-to-r from-red-50 to-amber-50 border border-red-100">
          <p className="text-[11.5px] text-slate-700 font-medium text-center leading-relaxed">
            Segun McKinsey, las empresas que no optimizan su pricing pierden entre un{' '}
            <span className="font-extrabold text-red-600">15% y 30%</span> de sus márgenes
            potenciales cada ano. Eso es dinero que se deja sobre la mesa en cada cotización.
          </p>
        </div>

        <PageFooter page={2} />
      </section>

      {/* ─── PAGE 3: SOLUCION - FEATURES ─── */}
      <section className="h-[297mm] px-14 py-12 flex flex-col break-before-page">
        <div className="text-xs font-bold tracking-[3px] uppercase text-indigo-500 mb-2">
          La Solución
        </div>
        <h2 className="text-[28px] font-extrabold text-slate-900 leading-tight mb-2">
          Todo lo que necesitas
          <br />
          en una plataforma
        </h2>
        <p className="text-[12px] text-slate-500 max-w-[520px] mb-6 leading-relaxed">
          Prixia integra cotizaciones inteligentes, pricing dinamico, inteligencia artificial y
          analytics avanzado en una única plataforma SaaS multi-tenant. Elimina Excel, elimina
          errores, y dale a tu equipo comercial las herramientas para vender más y mejor.
        </p>

        <div className="grid grid-cols-3 gap-4 flex-1 content-start">
          {[
            {
              icon: FileText,
              title: 'Cotizaciones Inteligentes',
              desc: 'Crea propuestas profesionales con multiples secciones, cada una con su propio modelo de pricing. Calculos en tiempo real, PDFs con tu branding y duplicación rápida.',
              gradient: 'from-blue-500 to-cyan-400',
            },
            {
              icon: LineChart,
              title: 'Pricing Dinamico',
              desc: 'Monitoreo automático 24/7 de precios de competidores en Amazon, MercadoLibre, Shopify, VTEX y Magento. Alertas instantaneas cuando alguien cambia un precio.',
              gradient: 'from-teal-500 to-emerald-400',
            },
            {
              icon: Ship,
              title: 'Tracking Importaciones',
              desc: 'Controla costos landed con desglose de aranceles, fletes, seguros e incoterms por línea. Visualización 3D de contenedores con detalle por item y actualización de costos en tiempo real.',
              gradient: 'from-indigo-500 to-blue-400',
            },
            {
              icon: Bot,
              title: 'Asistente IA',
              desc: 'Inteligencia artificial que predice win rate, recomienda el precio optimo para cerrar, simula escenarios de descuento y genera insights estrategicos de tu pipeline.',
              gradient: 'from-orange-500 to-amber-400',
            },
            {
              icon: BarChart3,
              title: 'Analytics Avanzado',
              desc: 'Dashboards ejecutivos con revenue pipeline, márgenes por línea, win/loss rate, tendencias por vendedor, leaderboard y análisis de descuentos vs. politica comercial.',
              gradient: 'from-pink-500 to-rose-400',
            },
            {
              icon: BadgeDollarSign,
              title: 'Multi-moneda',
              desc: 'Cotiza en USD, BOB, EUR o cualquier moneda. Tipos de cambio actualizados automáticamente. Conversión transparente en reportes y analytics consolidados.',
              gradient: 'from-lime-500 to-green-400',
            },
            {
              icon: ShieldCheck,
              title: 'Workflow de Aprobaciones',
              desc: 'Configura reglas automaticas por monto, porcentaje de descuento o margen mínimo. Cadenas de aprobación multi-nivel con notificaciones y historial completo.',
              gradient: 'from-violet-500 to-purple-400',
            },
            {
              icon: Calculator,
              title: '16+ Modelos de Pricing',
              desc: 'Cost-plus, margen sobre venta, markup, por usuario, por volumen, configurador, escalonado y más. Variables calculadas con formulas personalizadas entre modelos.',
              gradient: 'from-cyan-500 to-blue-400',
            },
            {
              icon: Zap,
              title: 'Integraciones Enterprise',
              desc: 'Conecta con SAP S/4HANA, SAP C4C, Salesforce, Odoo, HubSpot, Shopify y más. API REST publica documentada para integraciones custom con tu ecosistema.',
              gradient: 'from-amber-500 to-orange-400',
            },
          ].map((f) => (
            <div key={f.title} className="p-4 rounded-xl border border-slate-100 bg-white">
              <div
                className={`w-9 h-9 rounded-lg bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-2.5`}
              >
                <f.icon className="w-4.5 h-4.5 text-white" strokeWidth={2.2} />
              </div>
              <h3 className="font-bold text-slate-900 text-[11px] mb-1">{f.title}</h3>
              <p className="text-[9.5px] text-slate-500 leading-[1.65]">{f.desc}</p>
            </div>
          ))}
        </div>

        <PageFooter page={3} />
      </section>

      {/* ─── PAGE 4: COTIZACIONES + APROBACIONES ─── */}
      <section className="h-[297mm] px-14 py-12 flex flex-col break-before-page">
        <div className="text-xs font-bold tracking-[3px] uppercase text-blue-500 mb-2">
          Motor de Cotizaciones
        </div>
        <h2 className="text-[26px] font-extrabold text-slate-900 leading-tight mb-2">
          Cotizaciones profesionales
          <br />
          en minutos, no en horas
        </h2>
        <p className="text-[12px] text-slate-500 max-w-[520px] mb-5 leading-relaxed">
          El motor de cotizaciones de Prixia permite a tu equipo crear propuestas complejas con
          multiples secciones, modelos de pricing independientes y calculos automaticos. Todo con un
          workflow de aprobación integrado que protege tus márgenes sin frenar el pipeline.
        </p>

        {/* Screenshot */}
        <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200 mb-5">
          <img src="/screenshots/23-quotes.png" alt="Cotizaciones" className="w-full h-auto" />
        </div>

        {/* Feature columns */}
        <div className="grid grid-cols-2 gap-6 flex-1">
          <div>
            <h3 className="font-bold text-slate-900 text-[13px] mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-500" /> Cotizaciones
            </h3>
            <div className="space-y-2">
              {[
                {
                  t: 'Multi-seccion',
                  d: 'Cada sección usa un modelo de pricing independiente (hardware, licencias, servicios)',
                },
                {
                  t: '16+ modelos',
                  d: 'Cost-plus, margen sobre venta, markup, por usuario, escalonado, configurador y más',
                },
                {
                  t: 'Variables calculadas',
                  d: 'Formulas personalizadas que referencian variables de otros modelos',
                },
                { t: 'Multi-moneda', d: 'Tipos de cambio automaticos con conversión transparente' },
                {
                  t: 'PDF profesional',
                  d: 'Propuestas con logo, branding y formato personalizado de tu empresa',
                },
                {
                  t: 'Materiales y catálogo',
                  d: 'Maestro de productos con costos, márgenes y precios de lista',
                },
              ].map((item) => (
                <div key={item.t} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                  <div>
                    <span className="text-[10px] font-semibold text-slate-800">{item.t}: </span>
                    <span className="text-[10px] text-slate-500">{item.d}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-bold text-slate-900 text-[13px] mb-3 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-violet-500" /> Aprobaciones
            </h3>
            <div className="space-y-2">
              {[
                {
                  t: 'Reglas automaticas',
                  d: 'Configura triggers por monto total, % descuento o margen mínimo',
                },
                {
                  t: 'Multi-nivel',
                  d: 'Cadenas de aprobación jerarquicas con escalamiento automático',
                },
                {
                  t: 'Notificaciones',
                  d: 'Email y notificaciones in-app en tiempo real al aprobador',
                },
                {
                  t: 'Aprobación movil',
                  d: 'Aprueba o rechaza desde cualquier dispositivo con un toque',
                },
                {
                  t: 'Historial auditado',
                  d: 'Registro completo de decisiones, comentarios y timestamps',
                },
                { t: 'Dashboard SLA', d: 'Métricas de tiempo de aprobación y cuellos de botella' },
              ].map((item) => (
                <div key={item.t} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-violet-500 shrink-0 mt-0.5" />
                  <div>
                    <span className="text-[10px] font-semibold text-slate-800">{item.t}: </span>
                    <span className="text-[10px] text-slate-500">{item.d}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Callout */}
        <div className="p-4 rounded-xl bg-gradient-to-r from-blue-50 to-violet-50 border border-blue-100 mt-4">
          <p className="text-[10.5px] text-slate-600 text-center leading-relaxed">
            <span className="font-bold text-slate-800">Resultado tipico:</span> Empresas reducen el
            tiempo de cotizacion de{' '}
            <span className="font-extrabold text-blue-600">4-6 horas a 15 minutos</span> y aumentan
            el volumen de propuestas 5x sin agregar personal.
          </p>
        </div>

        <PageFooter page={4} />
      </section>

      {/* ─── PAGE 5: ANALYTICS ─── */}
      <section className="h-[297mm] px-14 py-12 flex flex-col break-before-page">
        <div className="text-xs font-bold tracking-[3px] uppercase text-pink-500 mb-2">
          Analytics
        </div>
        <h2 className="text-[26px] font-extrabold text-slate-900 leading-tight mb-2">
          Decisiones respaldadas
          <br />
          por datos en tiempo real
        </h2>
        <p className="text-[12px] text-slate-500 max-w-[520px] mb-5 leading-relaxed">
          Dashboards ejecutivos que consolidan todas las métricas de tu operación comercial. Desde
          el revenue pipeline hasta el análisis de descuentos, cada número esta a un click de
          distancia para que dejes de adivinar y empieces a decidir con datos.
        </p>

        {/* Screenshot */}
        <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200 mb-5">
          <img src="/screenshots/27-analytics.png" alt="Analytics" className="w-full h-auto" />
        </div>

        {/* KPIs grid */}
        <div className="grid grid-cols-3 gap-3.5 mb-5">
          {[
            {
              label: 'Revenue Pipeline',
              desc: 'Valor total de cotizaciones activas con progreso por etapa. Proyección de cierre basada en probabilidad historica y win rate por segmento.',
              icon: TrendingUp,
              color: 'text-emerald-500 bg-emerald-50',
            },
            {
              label: 'Win Rate',
              desc: 'Tasa de cierre desglosada por vendedor, línea de negocio, rango de monto y periodo. Identifica patrones de éxito y replica lo que funciona.',
              icon: Target,
              color: 'text-blue-500 bg-blue-50',
            },
            {
              label: 'Márgenes por Línea',
              desc: 'Rentabilidad real desglosada por producto, cliente, vendedor y periodo. Detecta donde se erosionan márgenes antes de que sea tarde.',
              icon: BarChart3,
              color: 'text-violet-500 bg-violet-50',
            },
            {
              label: 'Tendencias',
              desc: 'Evolución temporal de métricas clave con comparativas mes a mes, trimestre a trimestre. Graficos interactivos con drill-down.',
              icon: LineChart,
              color: 'text-pink-500 bg-pink-50',
            },
            {
              label: 'Leaderboard',
              desc: 'Ranking de vendedores por revenue generado, win rate, margen promedio y volumen de cotizaciones. Gamificación para motivar al equipo.',
              icon: Star,
              color: 'text-amber-500 bg-amber-50',
            },
            {
              label: 'Descuentos',
              desc: 'Análisis de descuentos otorgados vs. politica comercial. Detección de patrones de descuento excesivo por vendedor o segmento.',
              icon: BadgeDollarSign,
              color: 'text-teal-500 bg-teal-50',
            },
          ].map((k) => (
            <div
              key={k.label}
              className="p-3 rounded-lg border border-slate-100 flex items-start gap-2.5"
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${k.color}`}
              >
                <k.icon className="w-4 h-4" />
              </div>
              <div>
                <div className="font-bold text-[10.5px] text-slate-900">{k.label}</div>
                <div className="text-[9px] text-slate-500 leading-[1.6]">{k.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 rounded-xl bg-gradient-to-r from-pink-50 to-violet-50 border border-pink-100">
          <p className="text-[10.5px] text-slate-600 text-center leading-relaxed">
            <span className="font-bold text-slate-800">Dato:</span> Las empresas que usan analytics
            de pricing mejoran sus margenes un{' '}
            <span className="font-extrabold text-pink-600">23% en promedio</span> en los primeros 6
            meses, según Deloitte Pricing Analytics Report.
          </p>
        </div>

        <PageFooter page={5} />
      </section>

      {/* ─── PAGE 6: IA ─── */}
      <section className="h-[297mm] px-14 py-12 flex flex-col break-before-page">
        <div className="text-xs font-bold tracking-[3px] uppercase text-orange-500 mb-2">
          Inteligencia Artificial
        </div>
        <h2 className="text-[26px] font-extrabold text-slate-900 leading-tight mb-2">
          Un estratega de pricing
          <br />
          que nunca duerme
        </h2>
        <p className="text-[12px] text-slate-500 max-w-[520px] mb-5 leading-relaxed">
          Prixia integra inteligencia artificial avanzada (powered by Claude) que analiza tus datos
          historicos, patrones de mercado y comportamiento de clientes para darte recomendaciones
          accionables en cada deal. No es un chatbot generico — es un asistente entrenado en pricing
          que entiende tu negocio.
        </p>

        {/* Screenshot */}
        <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200 mb-5">
          <img
            src="/screenshots/28-ai-intelligence.png"
            alt="IA Intelligence"
            className="w-full h-auto"
          />
        </div>

        {/* AI capabilities */}
        <div className="grid grid-cols-2 gap-4 flex-1 content-start">
          {[
            {
              icon: BrainCircuit,
              title: 'Predicción de Win Rate',
              desc: 'Analiza variables historicas (cliente, monto, margen, competencia, tiempo de respuesta) para estimar la probabilidad de cierre de cada cotización. Te dice donde enfocar tu energia para maximizar conversiones.',
              color: 'from-orange-500 to-amber-400',
            },
            {
              icon: Target,
              title: 'Precio Optimo',
              desc: 'Recomienda el precio exacto que maximiza la probabilidad de ganar el deal manteniendo el margen objetivo. Balancea competitividad y rentabilidad basandose en datos reales de tu historial.',
              color: 'from-violet-500 to-purple-400',
            },
            {
              icon: LayoutDashboard,
              title: 'Simulación de Escenarios',
              desc: 'Modela "que pasaria si" con distintos niveles de precio y descuento. Evalua el impacto en revenue, margen y win rate antes de enviar la propuesta al cliente.',
              color: 'from-blue-500 to-cyan-400',
            },
            {
              icon: MessageSquare,
              title: 'Asistente Conversacional',
              desc: 'Pregunta en lenguaje natural: "cual es mi margen promedio este mes?", "que clientes tienen más potencial de upsell?", "comparame estas dos cotizaciones". Respuestas instantaneas con datos reales.',
              color: 'from-teal-500 to-emerald-400',
            },
            {
              icon: Eye,
              title: 'Detección de Anomalias',
              desc: 'Identifica automáticamente precios fuera de rango, descuentos excesivos, márgenes negativos y patrones inusuales. Alertas proactivas antes de que un error llegue al cliente.',
              color: 'from-red-500 to-pink-400',
            },
            {
              icon: TrendingUp,
              title: 'Insights Automaticos',
              desc: 'Genera reportes y recomendaciones proactivas cada semana: oportunidades de repricing, tendencias emergentes, alertas de erosion de márgenes y sugerencias de cross-sell.',
              color: 'from-emerald-500 to-green-400',
            },
          ].map((ai) => (
            <div
              key={ai.title}
              className="p-3.5 rounded-xl border border-slate-100 flex items-start gap-3"
            >
              <div
                className={`w-9 h-9 rounded-lg bg-gradient-to-br ${ai.color} flex items-center justify-center shrink-0`}
              >
                <ai.icon className="w-4.5 h-4.5 text-white" strokeWidth={2.2} />
              </div>
              <div>
                <h3 className="font-bold text-[11px] text-slate-900 mb-0.5">{ai.title}</h3>
                <p className="text-[9.5px] text-slate-500 leading-[1.6]">{ai.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <PageFooter page={6} />
      </section>

      {/* ─── PAGE 7: PRICING DINAMICO + IMPORTACIONES ─── */}
      <section className="h-[297mm] px-14 py-12 flex flex-col break-before-page">
        <div className="text-xs font-bold tracking-[3px] uppercase text-teal-500 mb-2">
          Monitoreo y Control
        </div>
        <h2 className="text-[26px] font-extrabold text-slate-900 leading-tight mb-2">
          Control total sobre
          <br />
          precios y costos
        </h2>
        <p className="text-[12px] text-slate-500 max-w-[520px] mb-5 leading-relaxed">
          Monitorea precios de competidores en tiempo real y controla cada centavo de tus costos de
          importación. Dos modulos que trabajan juntos para que siempre cotices con datos
          actualizados y márgenes reales.
        </p>

        {/* Pricing Dinamico */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <LineChart className="w-5 h-5 text-teal-500" />
            <h3 className="font-bold text-[14px] text-slate-900">Pricing Dinamico</h3>
            <span className="text-[9px] text-slate-400 ml-1">
              Monitoreo de competencia en e-commerce
            </span>
          </div>
          <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200 mb-3">
            <img
              src="/screenshots/30-dynamic-pricing.png"
              alt="Pricing Dinamico"
              className="w-full h-auto"
            />
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {[
              {
                t: 'Scraping 24/7',
                d: 'Monitoreo automático en Amazon, MercadoLibre, Shopify, VTEX, Magento y tiendas custom via scraper generico Puppeteer',
              },
              {
                t: 'Alertas inteligentes',
                d: 'Notificaciones automaticas cuando un competidor cambia precios en tus categorías clave. Configura umbrales y frecuencia',
              },
              {
                t: 'Historial y tendencias',
                d: 'Graficos de evolución de precios por competidor con análisis de patrones estacionales y de comportamiento',
              },
            ].map((t) => (
              <div key={t.t} className="p-2.5 rounded-lg bg-teal-50/50 border border-teal-100/60">
                <div className="text-[10px] font-semibold text-slate-800 mb-0.5">{t.t}</div>
                <span className="text-[9px] text-slate-500 leading-[1.55]">{t.d}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Importaciones */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3">
            <Ship className="w-5 h-5 text-indigo-500" />
            <h3 className="font-bold text-[14px] text-slate-900">Tracking de Importaciones</h3>
            <span className="text-[9px] text-slate-400 ml-1">
              Control de costos landed y logistica
            </span>
          </div>
          <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200 mb-3">
            <img
              src="/screenshots/31-importaciones.png"
              alt="Importaciones"
              className="w-full h-auto"
            />
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {[
              {
                t: 'Costos landed completos',
                d: 'Desglose por aranceles, fletes maritimos/aereos/terrestres, seguros, gastos portuarios, IVA e impuestos adicionales por incoterm',
              },
              {
                t: 'Visualización 3D',
                d: 'Representación visual de contenedores con detalle de items, pesos, volumenes y distribución de costos por producto',
              },
              {
                t: 'Incoterms por línea',
                d: 'Soporte de EXW, FOB, CIF, DDP y más. Calculo automático de responsabilidades y costos según el incoterm de cada línea',
              },
            ].map((t) => (
              <div
                key={t.t}
                className="p-2.5 rounded-lg bg-indigo-50/50 border border-indigo-100/60"
              >
                <div className="text-[10px] font-semibold text-slate-800 mb-0.5">{t.t}</div>
                <span className="text-[9px] text-slate-500 leading-[1.55]">{t.d}</span>
              </div>
            ))}
          </div>
        </div>

        <PageFooter page={7} />
      </section>

      {/* ─── PAGE 8: ARQUITECTURA / PLATAFORMA ─── */}
      <section className="h-[297mm] px-14 py-12 flex flex-col break-before-page">
        <div className="text-xs font-bold tracking-[3px] uppercase text-indigo-500 mb-2">
          Plataforma
        </div>
        <h2 className="text-[26px] font-extrabold text-slate-900 leading-tight mb-2">
          Enterprise-ready
          <br />
          desde el día uno
        </h2>
        <p className="text-[12px] text-slate-500 max-w-[520px] mb-6 leading-relaxed">
          Prixia esta disenada para escalar con tu empresa. Arquitectura multi-tenant, seguridad
          enterprise, integraciones robustas y una API completa para que tu equipo de TI la adopte
          con confianza.
        </p>

        {/* Architecture features */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {[
            {
              icon: Layers,
              title: 'Multi-tenant SaaS',
              desc: 'Cada empresa tiene su propio espacio aislado con datos, configuraciones y usuarios independientes. Desplegado en la nube con alta disponibilidad y backups automaticos. Escalamiento horizontal sin limites.',
              color: 'bg-indigo-50 text-indigo-500 border-indigo-100',
            },
            {
              icon: Lock,
              title: 'Seguridad Enterprise',
              desc: 'Autenticación JWT con refresh tokens, control de acceso por roles (Admin, Salesperson, Approver, Platform Admin), encriptación en transito y en reposo, y logs de auditoria completos de cada acción.',
              color: 'bg-emerald-50 text-emerald-500 border-emerald-100',
            },
            {
              icon: Globe,
              title: 'API REST Publica',
              desc: 'API documentada con OpenAPI/Swagger para integrar Prixia con tu ecosistema. Endpoints para cotizaciones, materiales, clientes, modelos de pricing y analytics. Webhooks para eventos en tiempo real.',
              color: 'bg-blue-50 text-blue-500 border-blue-100',
            },
            {
              icon: Workflow,
              title: '4 Roles de Usuario',
              desc: 'Platform Admin (gestión global), Company Admin (configuración tenant), Salesperson (cotizaciones y clientes) y Approver (flujos de aprobación). Permisos granulares por modulo y acción.',
              color: 'bg-violet-50 text-violet-500 border-violet-100',
            },
            {
              icon: RefreshCw,
              title: 'Sincronización Automatica',
              desc: 'Workers en background (BullMQ + Redis) para sincronización de datos con SAP, scraping de precios, actualización de tipos de cambio y vencimiento automático de cotizaciones.',
              color: 'bg-amber-50 text-amber-500 border-amber-100',
            },
            {
              icon: FileBarChart,
              title: 'Reportes y Exportación',
              desc: 'Exporta cualquier vista a PDF o Excel. Reportes programados por email. Dashboard ejecutivo con KPIs consolidados para la gerencia y datos drill-down para el equipo operativo.',
              color: 'bg-pink-50 text-pink-500 border-pink-100',
            },
          ].map((f) => (
            <div
              key={f.title}
              className={`rounded-xl border p-4 flex items-start gap-3 ${f.color.split(' ')[0]} ${f.color.split(' ').slice(2).join(' ')}`}
            >
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${f.color}`}
              >
                <f.icon className="w-4.5 h-4.5" />
              </div>
              <div>
                <h3 className="font-bold text-[11.5px] text-slate-900 mb-0.5">{f.title}</h3>
                <p className="text-[9.5px] text-slate-500 leading-[1.6]">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Integrations */}
        <div>
          <h3 className="font-bold text-[13px] text-slate-900 mb-3">Integraciones Enterprise</h3>
          <div className="grid grid-cols-3 gap-3">
            {[
              {
                name: 'SAP S/4HANA',
                cat: 'ERP',
                desc: 'Sincronización bi-direccional de materiales, clientes y precios via OData',
                color: 'bg-blue-500',
              },
              {
                name: 'SAP C4C',
                cat: 'CRM',
                desc: 'Sync de oportunidades, contactos y actividades comerciales',
                color: 'bg-sky-500',
              },
              {
                name: 'Salesforce',
                cat: 'CRM',
                desc: 'Integración de pipeline, leads y cotizaciones via REST API',
                color: 'bg-cyan-500',
              },
              {
                name: 'Odoo',
                cat: 'ERP',
                desc: 'Sincronización de productos, ordenes y datos maestros',
                color: 'bg-violet-500',
              },
              {
                name: 'HubSpot',
                cat: 'CRM',
                desc: 'Contacts, deals y automatizaciones de marketing',
                color: 'bg-orange-500',
              },
              {
                name: 'Shopify',
                cat: 'E-commerce',
                desc: 'Monitoreo de precios y catálogo de productos online',
                color: 'bg-green-500',
              },
              {
                name: 'VTEX',
                cat: 'E-commerce',
                desc: 'Scraping de precios y disponibilidad de competidores',
                color: 'bg-pink-500',
              },
              {
                name: 'Magento',
                cat: 'E-commerce',
                desc: 'Monitoreo de catálogo y precios de tiendas online',
                color: 'bg-amber-500',
              },
              {
                name: 'API REST',
                cat: 'Custom',
                desc: 'API publica documentada para cualquier integración',
                color: 'bg-slate-500',
              },
            ].map((i) => (
              <div
                key={i.name}
                className="flex items-start gap-2.5 p-2.5 rounded-lg border border-slate-100"
              >
                <div
                  className={`w-7 h-7 rounded-md ${i.color} flex items-center justify-center shrink-0 mt-0.5`}
                >
                  <span className="text-white font-bold text-[8px]">
                    {i.name.slice(0, 3).toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-[10px] text-slate-800">
                    {i.name} <span className="text-[8px] text-slate-400">({i.cat})</span>
                  </div>
                  <div className="text-[8.5px] text-slate-500">{i.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <PageFooter page={8} />
      </section>

      {/* ─── PAGE 9: CASOS DE EXITO ─── */}
      <section className="h-[297mm] px-14 py-12 flex flex-col break-before-page">
        <div className="text-xs font-bold tracking-[3px] uppercase text-emerald-500 mb-2">
          Casos de Éxito
        </div>
        <h2 className="text-[26px] font-extrabold text-slate-900 leading-tight mb-2">
          Resultados reales
          <br />
          de empresas reales
        </h2>
        <p className="text-[12px] text-slate-500 max-w-[520px] mb-5 leading-relaxed">
          Empresas de diferentes industrias y tamanos ya transformaron su proceso comercial con
          Prixia. Estos son sus resultados medibles en los primeros meses de implementación.
        </p>

        <div className="space-y-4 flex-1">
          {[
            {
              company: 'Importadora Andina',
              segment: 'Retail / Importadores',
              challenge:
                'Precios desactualizados, 3 dias/semana actualizando Excel, errores que generaban ventas bajo costo.',
              result:
                'Márgenes mejoraron 15%, tiempo de respuesta 3x más rápido, errores reducidos 40%.',
              quote:
                'Antes viviamos apagando incendios con los precios; ahora tenemos una estrategia clara y datos en tiempo real.',
              author: 'Carlos M., Gerente Comercial',
              metrics: [
                { v: '15%', l: 'Mejora márgenes' },
                { v: '3x', l: 'Más rápido' },
                { v: '40%', l: 'Menos errores' },
              ],
            },
            {
              company: 'TechSolutions Bolivia',
              segment: 'B2B / Tecnologia',
              challenge:
                'Cotizaciones complejas de 4-6 horas cada una, limitados a 30/mes. Errores en márgenes compuestos.',
              result:
                'De 30 a 150+ cotizaciones/mes, 80% menos tiempo, ticket promedio aumento 25%.',
              quote:
                'Nuestros ingenieros de preventa pasaron de ser operadores de Excel a verdaderos asesores de tecnologia.',
              author: 'Ana L., Directora de Operaciones',
              metrics: [
                { v: '80%', l: 'Menos tiempo' },
                { v: '150+', l: 'Cotiz./mes' },
                { v: '25%', l: 'Mayor ticket' },
              ],
            },
            {
              company: 'DataCore Systems',
              segment: 'Tecnologia / Software',
              challenge:
                '30% de propuestas con errores de calculo, $45K de revenue perdido por trimestre.',
              result:
                'Errores reducidos 90%, productividad 4x, win rate mejoro 12%, $45K recuperados.',
              quote:
                'Ahora tenemos total confianza en cada propuesta que enviamos, y eso se nota en la tasa de cierre.',
              author: 'Fernando G., CTO',
              metrics: [
                { v: '90%', l: 'Menos errores' },
                { v: '4x', l: 'Productividad' },
                { v: '$45K', l: 'Recuperados' },
              ],
            },
            {
              company: 'Consultora Altiplano',
              segment: 'Servicios / Consultoria',
              challenge:
                'Hasta 40% de diferencia en precios entre vendedores. Descuentos por WhatsApp sin registro.',
              result:
                '100% consistencia en precios, márgenes mejoraron 18%, cero descuentos no autorizados.',
              quote:
                'Prixia nos dio algo que no teniamos: control. Márgenes protegidos y proceso de aprobación que realmente funciona.',
              author: 'Roberto P., CEO',
              metrics: [
                { v: '100%', l: 'Consistencia' },
                { v: '18%', l: 'Más margen' },
                { v: '0', l: 'Desc. no autoriz.' },
              ],
            },
          ].map((c) => (
            <div key={c.company} className="p-4 rounded-xl border border-slate-100 bg-white">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-bold text-[12px] text-slate-900">{c.company}</h3>
                  <span className="text-[9px] text-slate-400">{c.segment}</span>
                </div>
                <div className="flex gap-2">
                  {c.metrics.map((m) => (
                    <div
                      key={m.l}
                      className="text-center px-2 py-1 rounded-lg bg-slate-50 border border-slate-100"
                    >
                      <div className="text-[12px] font-extrabold text-indigo-600">{m.v}</div>
                      <div className="text-[7.5px] text-slate-500">{m.l}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-2">
                <div className="text-[9px]">
                  <span className="font-semibold text-slate-700">Desafio:</span>{' '}
                  <span className="text-slate-500">{c.challenge}</span>
                </div>
                <div className="text-[9px]">
                  <span className="font-semibold text-slate-700">Resultado:</span>{' '}
                  <span className="text-slate-500">{c.result}</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Quote className="w-3.5 h-3.5 text-slate-300 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[9.5px] text-slate-600 italic leading-relaxed">{c.quote}</p>
                  <p className="text-[9px] text-slate-400 mt-1 font-medium">{c.author}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <PageFooter page={9} />
      </section>

      {/* ─── PAGE 10: PRICING ─── */}
      <section className="h-[297mm] px-14 py-12 flex flex-col break-before-page">
        <div className="text-xs font-bold tracking-[3px] uppercase text-indigo-500 mb-2">
          Planes y Precios
        </div>
        <h2 className="text-[26px] font-extrabold text-slate-900 leading-tight mb-2">
          Un plan para cada
          <br />
          etapa de tu empresa
        </h2>
        <p className="text-[12px] text-slate-500 max-w-[520px] mb-6 leading-relaxed">
          Planes flexibles que escalan con tu equipo. Todos incluyen acceso completo a la plataforma
          con cotizaciones ilimitadas. Sin costos ocultos. Facturación mensual o anual con 20% de
          descuento.
        </p>

        {/* Pricing tiers */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
            {
              name: 'Starter',
              price: '$99',
              period: '/mes',
              desc: 'Para equipos pequenos de hasta 5 personas con ventas directas y catalogos simples.',
              features: [
                'Hasta 5 usuarios',
                'Cotizaciones ilimitadas',
                'Asistente IA basico',
                'Catálogo de productos',
                'Modelos de pricing',
                'Multi-moneda',
                'PDF con branding',
                'Soporte por email',
              ],
              highlighted: false,
            },
            {
              name: 'Pro',
              price: '$199',
              period: '/mes',
              desc: 'Para empresas con cotizaciones complejas, multiples líneas y flujos de aprobación.',
              features: [
                'Hasta 15 usuarios',
                'Cotizaciones ilimitadas',
                'Asistente IA completo',
                'Workflow de aprobaciones',
                'Analytics avanzado',
                'Integraciones API',
                'Modelos ilimitados',
                'Pricing dinamico',
                'Soporte prioritario',
              ],
              highlighted: true,
            },
            {
              name: 'Enterprise',
              price: '$399',
              period: '/mes',
              desc: 'Para organizaciones grandes con necesidades de personalización y multiples sucursales.',
              features: [
                'Usuarios ilimitados',
                'Todo del plan Pro',
                'IA personalizada',
                'Multi-sucursal',
                'Reportes ejecutivos',
                'API completa',
                'Import tracking',
                'Capacitación incluida',
                'Soporte 24/7',
              ],
              highlighted: false,
            },
          ].map((t) => (
            <div
              key={t.name}
              className={`rounded-xl p-5 flex flex-col ${
                t.highlighted
                  ? 'bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-lg ring-2 ring-indigo-300'
                  : 'bg-white border border-slate-200'
              }`}
            >
              {t.highlighted && (
                <span className="text-[9px] font-bold bg-white/20 rounded-full px-2.5 py-0.5 self-start mb-2">
                  Más Popular
                </span>
              )}
              <h3
                className={`font-bold text-[14px] ${t.highlighted ? 'text-white' : 'text-slate-900'}`}
              >
                {t.name}
              </h3>
              <div className="flex items-baseline gap-0.5 mt-1 mb-1">
                <span
                  className={`text-[28px] font-extrabold ${t.highlighted ? 'text-white' : 'text-slate-900'}`}
                >
                  {t.price}
                </span>
                <span
                  className={`text-[11px] ${t.highlighted ? 'text-indigo-200' : 'text-slate-400'}`}
                >
                  {t.period}
                </span>
              </div>
              <p
                className={`text-[9.5px] mb-3 leading-relaxed ${t.highlighted ? 'text-indigo-200' : 'text-slate-500'}`}
              >
                {t.desc}
              </p>
              <div className="space-y-1.5 flex-1">
                {t.features.map((f) => (
                  <div key={f} className="flex items-center gap-1.5">
                    <Check
                      className={`w-3 h-3 shrink-0 ${t.highlighted ? 'text-indigo-200' : 'text-indigo-500'}`}
                    />
                    <span
                      className={`text-[9.5px] ${t.highlighted ? 'text-indigo-100' : 'text-slate-600'}`}
                    >
                      {f}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Services */}
        <div className="mb-5">
          <h3 className="font-bold text-[13px] text-slate-900 mb-3">Servicios Profesionales</h3>
          <div className="grid grid-cols-4 gap-3">
            {[
              {
                name: 'Setup + Configuración',
                price: '$500',
                desc: 'Instalación completa, configuración de productos, márgenes, usuarios y permisos',
              },
              {
                name: 'Capacitacion',
                price: '$300',
                desc: 'Entrenamiento presencial o remoto para tu equipo de ventas y administradores',
              },
              {
                name: 'Migración de Datos',
                price: '$200',
                desc: 'Importamos tus catalogos, clientes, precios y datos historicos desde Excel o CSV',
              },
              {
                name: 'Integración Custom',
                price: 'Desde $500',
                desc: 'Conectamos Prixia con tu ERP, CRM o sistema de facturación existente',
              },
            ].map((sv) => (
              <div key={sv.name} className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <div className="font-bold text-[12px] text-indigo-600 mb-0.5">{sv.price}</div>
                <div className="font-semibold text-[10px] text-slate-800">{sv.name}</div>
                <div className="text-[8.5px] text-slate-500 mt-0.5 leading-[1.5]">{sv.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Industries */}
        <div>
          <h3 className="font-bold text-[13px] text-slate-900 mb-3">Industrias que Servimos</h3>
          <div className="grid grid-cols-4 gap-3">
            {[
              {
                name: 'Distribuidoras e Importadoras',
                desc: 'Control de costos de importación, monitoreo de competidores y cotizaciones con márgenes reales sobre costo landed',
                icon: Ship,
                gradient: 'from-blue-500 to-cyan-400',
              },
              {
                name: 'Tecnologia y Reventa',
                desc: 'Cotizaciones multi-componente con pricing de licencias, hardware y servicios profesionales',
                icon: Calculator,
                gradient: 'from-violet-500 to-purple-400',
              },
              {
                name: 'Manufactura',
                desc: 'Formulas de pricing con variables de producción, materia prima y costos operativos',
                icon: Zap,
                gradient: 'from-emerald-500 to-green-400',
              },
              {
                name: 'Comercio Mayorista',
                desc: 'Pricing por volumen, descuentos controlados y monitoreo de precios en e-commerce',
                icon: CircleDollarSign,
                gradient: 'from-orange-500 to-amber-400',
              },
            ].map((ind) => (
              <div key={ind.name} className="p-3 rounded-lg border border-slate-100">
                <div
                  className={`w-7 h-7 rounded-md bg-gradient-to-br ${ind.gradient} flex items-center justify-center mb-2`}
                >
                  <ind.icon className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="font-semibold text-[10px] text-slate-800 mb-0.5">{ind.name}</div>
                <div className="text-[8.5px] text-slate-500 leading-[1.5]">{ind.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <PageFooter page={10} />
      </section>

      {/* ─── PAGE 11: CTA ─── */}
      <section className="h-[297mm] relative overflow-hidden flex flex-col items-center justify-center break-before-page">
        {/* Full gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-700 via-indigo-600 to-violet-600" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute bottom-20 -left-20 w-64 h-64 rounded-full bg-white/5" />
        <div className="absolute top-1/3 right-1/4 w-40 h-40 rounded-full bg-white/10" />

        <div className="relative z-10 text-center px-14 max-w-[500px]">
          {/* Logo */}
          <div className="mb-8">
            <PrixiaLogo variant="white" size="lg" />
          </div>

          <div className="text-sm font-semibold tracking-[3px] uppercase text-indigo-200 mb-4">
            Comienza Hoy
          </div>
          <h2 className="text-[40px] font-extrabold text-white leading-tight mb-4">
            Deja de perder ventas.
            <br />
            Transforma tu pricing.
          </h2>
          <p className="text-[14px] text-indigo-200 mx-auto mb-8 leading-relaxed">
            Agenda una demo personalizada de 30 minutos y descubre como Prixia puede mejorar tus
            márgenes, acelerar tu proceso de cotización y darte visibilidad total sobre precios de
            competidores y costos de importación.
          </p>

          {/* CTA button */}
          <div className="inline-flex items-center gap-2.5 bg-white text-indigo-700 font-bold text-[15px] px-8 py-3.5 rounded-xl shadow-lg">
            Agenda tu Demo Gratis
            <ArrowRight className="w-5 h-5" />
          </div>

          <div className="mt-6 flex items-center justify-center gap-6 text-indigo-200 text-[11px]">
            <div className="flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> 14 días de prueba gratis
            </div>
            <div className="flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Sin tarjeta de crédito
            </div>
            <div className="flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Setup en menos de 24 hrs
            </div>
          </div>

          {/* Contact info */}
          <div className="mt-12 space-y-2">
            <div className="text-indigo-100 text-[13px] font-semibold">ventas@prixia.com</div>
            <div className="text-indigo-200 text-[12px]">prixia.com</div>
          </div>
        </div>

        <div className="absolute bottom-4 left-14 right-14 flex justify-between text-[8px] text-indigo-300/60">
          <PrixiaLogo variant="white" size="sm" />
          <span>prixia.com</span>
          <span>11</span>
        </div>
      </section>
    </div>
  );
}
